import { CommonModule } from '@angular/common';
import { Component, DestroyRef, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription, interval } from 'rxjs';
import { MaterialModule } from 'src/app/material.module';

export const OTP_RESEND_WAIT_SECONDS = 60;
export const OTP_MAX_RESENDS = 2;

// Resend OTP control shared by every ABHA OTP step: it unlocks 60 seconds after an OTP is
// sent and allows at most 2 resends (NHA M1 CRT_ABHA_106). Render it only while an OTP is
// awaited, so creating it starts the wait.
@Component({
  selector: 'app-abha-otp-resend',
  standalone: true,
  imports: [CommonModule, MaterialModule, TranslateModule],
  templateUrl: './abha-otp-resend.component.html',
})
export class AbhaOtpResendComponent implements OnInit {
  @Input() isBusy = false;
  @Output() readonly resend = new EventEmitter<void>();

  secondsLeft = OTP_RESEND_WAIT_SECONDS;
  resendsLeft = OTP_MAX_RESENDS;
  private countdown?: Subscription;
  private readonly destroyRef = inject(DestroyRef);

  get canResend(): boolean {
    return this.secondsLeft === 0 && this.resendsLeft > 0 && !this.isBusy;
  }

  ngOnInit(): void {
    this.startCountdown();
  }

  onResend(): void {
    if (!this.canResend) {
      return;
    }

    this.resendsLeft -= 1;
    this.startCountdown();
    this.resend.emit();
  }

  private startCountdown(): void {
    this.countdown?.unsubscribe();
    this.secondsLeft = OTP_RESEND_WAIT_SECONDS;
    this.countdown = interval(1000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.tick());
  }

  private tick(): void {
    this.secondsLeft = Math.max(0, this.secondsLeft - 1);
    if (this.secondsLeft === 0) {
      this.countdown?.unsubscribe();
    }
  }
}
